import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchMyRevenuePeriods } from '../../../service/api/revenue';
import DatePicker from '../../../components/common/DatePicker';
import Select, { SelectOption } from '../../../components/common/Select';
import { formatDate, formatMoney } from './utils';

interface DataTabProps {
  project: Api.Project.ProjectDetailResponse;
  onRefresh?: () => void;
}

export default function DataTab({ project }: DataTabProps) {
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [periods, setPeriods] = useState<Api.Revenue.MyRevenuePeriodResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const channels = project.channels || [];

  const channelOptions: SelectOption[] = useMemo(() => {
    return [
      { label: '全部渠道', value: '' },
      ...channels.map((ch, idx) => {
        const chId = ch.id || ch.channel_id || String(idx);
        const channelName = ch.channel?.name || `渠道 ${idx + 1}`;
        return {
          label: `${channelName}${ch.region?.name ? ` (${ch.region.name})` : ''}`,
          value: chId
        };
      })
    ];
  }, [channels]);

  // 记住上一次成功发起的 requestKey 避免多次初始化渲染重复请求
  const lastFetchKeyRef = useRef<string>('');

  const loadRevenuePeriods = useCallback(async (force = false) => {
    if (!project?.id) return;

    const requestKey = `${project.id}_${selectedChannelId}_${startDate}_${endDate}`;
    if (!force && lastFetchKeyRef.current === requestKey) {
      return;
    }
    lastFetchKeyRef.current = requestKey;

    setLoading(true);
    setError('');
    try {
      const params: Api.Revenue.MyRevenuePeriodsParams = {
        project_id: project.id,
        page_size: 100
      };
      if (selectedChannelId) {
        params.project_channel_id = selectedChannelId;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }

      const res = await fetchMyRevenuePeriods(params);
      if (res && Array.isArray(res.items)) {
        setPeriods(res.items);
      } else if (Array.isArray(res)) {
        setPeriods(res as unknown as Api.Revenue.MyRevenuePeriodResponse[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取收入账期数据失败');
      lastFetchKeyRef.current = '';
    } finally {
      setLoading(false);
    }
  }, [project?.id, selectedChannelId, startDate, endDate]);

  useEffect(() => {
    loadRevenuePeriods();
  }, [loadRevenuePeriods]);

  const handleStartDateChange = (val: string) => {
    setStartDate(val);
    if (val && endDate && val > endDate) {
      setEndDate('');
    }
  };

  const handleEndDateChange = (val: string) => {
    setEndDate(val);
    if (val && startDate && val < startDate) {
      setStartDate('');
    }
  };

  const handleReset = () => {
    setSelectedChannelId('');
    setStartDate('');
    setEndDate('');
  };

  /** 收入周期状态渲染 (对照 revenue_period_status 枚举) */
  const renderPeriodStatusBadge = (status?: string) => {
    switch (status) {
      case 'DRAFT':
        return (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200 font-medium">
            未复核草稿
          </span>
        );
      case 'PENDING_REVIEW':
        return (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 font-medium">
            等待复核
          </span>
        );
      case 'APPROVED':
        return (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium">
            周期已确认
          </span>
        );
      case 'VOID':
        return (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200 font-medium">
            已作废
          </span>
        );
      default:
        return (
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 font-medium">
            {status || '正常'}
          </span>
        );
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground-950 text-sm">收入周期数据列表</h3>
          <p className="text-xs text-foreground-500 mt-1">查看项目各月度收入周期数据明细与结算汇率</p>
        </div>
        <button
          onClick={() => loadRevenuePeriods(true)}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-background-300 hover:bg-gray-50 text-foreground-700 flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          刷新数据
        </button>
      </div>

      {/* 筛选条件栏 */}
      <div className="mb-6 bg-background-50/60 p-4 rounded-xl border border-background-200/50 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex flex-wrap items-center gap-4">
          {/* 项目渠道筛选 */}
          {channels.length > 0 && (
            <div className="flex items-center gap-1.5">
              <label className="text-foreground-500 font-medium whitespace-nowrap">项目渠道:</label>
              <div className="w-44">
                <Select
                  options={channelOptions}
                  value={selectedChannelId}
                  onChange={(val) => setSelectedChannelId(String(val))}
                  placeholder="全部渠道"
                  size="sm"
                />
              </div>
            </div>
          )}

          {/* 收益日期范围 */}
          <div className="flex items-center gap-1.5">
            <label className="text-foreground-500 font-medium whitespace-nowrap">收益日期:</label>
            <div className="w-36">
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                placeholder="开始日期"
                maxDate={endDate || undefined}
                size="sm"
                clearable
              />
            </div>
            <span className="text-foreground-400">-</span>
            <div className="w-36">
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                placeholder="结束日期"
                minDate={startDate || undefined}
                size="sm"
                clearable
              />
            </div>
          </div>
        </div>

        {/* 重置按钮 */}
        {(selectedChannelId || startDate || endDate) && (
          <button
            onClick={handleReset}
            className="text-xs px-2.5 py-1.5 text-foreground-500 hover:text-foreground-800 transition-colors flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        )}
      </div>

      {/* Revenue Periods Table */}
      {loading ? (
        <div className="py-12 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-loader-4-line text-3xl block mb-2 text-foreground-400 animate-spin"></i>
          正在加载收入周期数据...
        </div>
      ) : error ? (
        <div className="py-10 text-center text-rose-500 text-sm glass-card rounded-xl">
          <i className="ri-error-warning-line text-3xl block mb-2 text-rose-400"></i>
          {error}
        </div>
      ) : periods.length === 0 ? (
        <div className="py-12 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-file-chart-line text-3xl block mb-2 text-foreground-300"></i>
          暂无收入周期数据记录
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-gray-50/50">
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">收益日期</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-foreground-500">总收入 (原币/结算币)</th>
                <th className="text-right py-3 px-4 text-xs font-medium text-foreground-500">渠道扣除 (原币/结算币)</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">适用汇率</th>
                <th className="text-center py-3 px-4 text-xs font-medium text-foreground-500">周期状态</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-foreground-500">复核时间</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p, idx) => (
                <tr key={p.id || idx} className="border-b border-background-200/50 hover:bg-gray-50/50 transition-colors">
                  <td className="py-3 px-4 text-xs font-semibold text-foreground-900">
                    {formatDate(p.revenue_date)}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <p className="font-semibold text-foreground-950">
                      {formatMoney(p.gross_income_settlement ?? p.gross_income_original, p.settlement_currency || p.original_currency)}
                    </p>
                    {p.original_currency && p.original_currency !== p.settlement_currency && (
                      <p className="text-[11px] text-foreground-400">
                        原币: {formatMoney(p.gross_income_original, p.original_currency)}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <p className="font-medium text-rose-600">
                      -{formatMoney(p.channel_deduction_settlement ?? p.channel_deduction_original, p.settlement_currency || p.original_currency)}
                    </p>
                    {p.original_currency && p.original_currency !== p.settlement_currency && (
                      <p className="text-[11px] text-foreground-400">
                        原币: -{formatMoney(p.channel_deduction_original, p.original_currency)}
                      </p>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-foreground-600">
                    {p.exchange_rate ? (
                      <div>
                        <p className="font-mono">{p.exchange_rate}</p>
                        <p className="text-[11px] text-foreground-400">{p.exchange_rate_source || '官方标准汇率'}</p>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {renderPeriodStatusBadge(p.status)}
                  </td>
                  <td className="py-3 px-4 text-xs text-foreground-500">
                    {formatDate(p.reviewed_at || p.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
